"use client";

import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@shared/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { toast } from "@shared/components/ui/snackbar";
import { IconUserPlus, IconUserShare } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ADMIN_DEFAULT_INVITER } from "@/components/admin.users.constants";
import { useAdminAllUsers } from "@/lib/admin/directory.hooks";
import { useAdminCreateUser, useAdminEmailExists } from "@/lib/admin/users.hooks";
import { useAuth } from "@/lib/auth";
import { graphqlErrorMessage } from "@/lib/graphql/client";
import { cn } from "@/lib/utils";

const nativeSelectClass = cn(
  "border-input dark:bg-input/30 flex h-9 w-full min-w-0 appearance-none rounded-md border bg-transparent py-1 pl-3 pr-8 text-sm shadow-xs transition-[color,box-shadow] outline-none",
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
);

interface AdminUsersCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AdminUsersCreateDialog = ({ open, onClose }: AdminUsersCreateDialogProps) => {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const createUser = useAdminCreateUser();
  const usersQuery = useAdminAllUsers(false, open);

  const [email, setEmail] = useState("");
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [hubspotContactId, setHubspotContactId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [invite, setInvite] = useState(true);
  const [inviteMethod, setInviteMethod] = useState<"SIGNUP_INVITE" | "PASSWORD_RESET">("SIGNUP_INVITE");
  const [invitedBy, setInvitedBy] = useState(ADMIN_DEFAULT_INVITER);
  const [invitedByBlurred, setInvitedByBlurred] = useState(false);
  const [creditsUsd, setCreditsUsd] = useState("");

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Email existence check — only runs after first blur and when email looks valid
  const emailLooksValid = EMAIL_RE.test(email.trim());
  const emailExistsQuery = useAdminEmailExists(email.trim(), emailBlurred && emailLooksValid);
  const emailAlreadyExists = emailExistsQuery.data?.adminEmailExists === true;

  // Inviter email existence check — warn (not block) if the email is not a known user
  const invitedByTrimmed = invitedBy.trim();
  const invitedByLooksValid = EMAIL_RE.test(invitedByTrimmed);
  const inviterExistsQuery = useAdminEmailExists(invitedByTrimmed, invite && invitedByBlurred && invitedByLooksValid);
  // Only SIGNUP_INVITE sends via the WorkOS inviter — PASSWORD_RESET uses a direct link and doesn't need this check.
  const inviterNotFound =
    inviteMethod === "SIGNUP_INVITE" && invitedByBlurred && inviterExistsQuery.data?.adminEmailExists === false;

  // Org combobox state
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgQuery, setOrgQuery] = useState("");
  const orgRef = useRef<HTMLDivElement>(null);

  // Build org map: id → { name, emails[], latestCreatedAt }
  const orgMap = useMemo(() => {
    const map = new Map<string, { name: string; emails: string[]; latestCreatedAt: string }>();
    for (const user of usersQuery.data?.adminAllUsers ?? []) {
      const existing = map.get(user.org_id);
      if (existing) {
        existing.emails.push(user.email);
        if (user.created_at > existing.latestCreatedAt) {
          existing.latestCreatedAt = user.created_at;
        }
      } else {
        map.set(user.org_id, {
          name: user.org_name,
          emails: [user.email],
          latestCreatedAt: user.created_at,
        });
      }
    }
    return map;
  }, [usersQuery.data]);

  // Sorted by most recently active, filtered by query, capped at 10
  const filteredOrgs = useMemo(() => {
    const sorted = Array.from(orgMap.entries()).sort(([, a], [, b]) =>
      b.latestCreatedAt.localeCompare(a.latestCreatedAt),
    );
    if (!orgQuery.trim()) return sorted.slice(0, 10);
    const q = orgQuery.toLowerCase();
    return sorted
      .filter(
        ([id, { name, emails }]) =>
          name.toLowerCase().includes(q) ||
          id.toLowerCase().includes(q) ||
          emails.some((e) => e.toLowerCase().includes(q)),
      )
      .slice(0, 10);
  }, [orgMap, orgQuery]);

  const selectedOrgName = orgId ? (orgMap.get(orgId)?.name ?? `${orgId.slice(0, 8)}…`) : "";

  // Close org dropdown on click outside
  useEffect(() => {
    if (!orgOpen) return;
    const handler = (e: MouseEvent) => {
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) {
        setOrgOpen(false);
        setOrgQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [orgOpen]);

  const handleOrgSelect = (id: string) => {
    setOrgId(id);
    setOrgOpen(false);
    setOrgQuery("");
  };

  const handleOrgClear = () => {
    setOrgId("");
    setOrgQuery("");
    setOrgOpen(true);
  };

  const reset = () => {
    setEmail("");
    setEmailBlurred(false);
    setFirstName("");
    setLastName("");
    setHubspotContactId("");
    setOrgId("");
    setOrgQuery("");
    setOrgOpen(false);
    setInvite(true);
    setInviteMethod("SIGNUP_INVITE");
    setInvitedBy(ADMIN_DEFAULT_INVITER);
    setInvitedByBlurred(false);
    setCreditsUsd("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required.");
      return;
    }
    if (emailAlreadyExists) {
      toast.error("A user with this email already exists.");
      return;
    }

    if (creditsUsd !== "" && !/^\d+$/.test(creditsUsd)) {
      toast.error("Credits must be a whole number (no decimals or special characters).");
      return;
    }

    createUser.mutate(
      {
        email: email.trim(),
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        hubspot_contact_id: hubspotContactId.trim() || null,
        org_id: orgId || null,
        jobtitle: null,
        invite,
        invite_method: invite ? inviteMethod : "NONE",
        invited_by_email: invite ? invitedBy.trim() || null : null,
        credits_usd: creditsUsd !== "" ? parseInt(creditsUsd, 10) : null,
      },
      {
        onSuccess: () => {
          toast.success(invite ? "Invite sent." : "User created.");
          void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
          handleClose();
        },
        onError: (error) =>
          toast.error(graphqlErrorMessage(error, invite ? "Failed to send invite." : "Failed to create user.")),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Creates the user in WorkOS and Postgres, and provisions a default project and billing record.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Email with blur validation */}
          <div className="grid gap-2">
            <Label htmlFor="create-email">Email</Label>
            <Input
              id="create-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailBlurred(false);
              }}
              onBlur={() => setEmailBlurred(true)}
              required
              aria-invalid={emailAlreadyExists}
              className={emailAlreadyExists ? "border-destructive focus-visible:ring-destructive/50" : undefined}
            />
            {emailAlreadyExists && <p className="text-destructive text-xs">A user with this email already exists.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="create-first-name">First name</Label>
              <Input id="create-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-last-name">Last name</Label>
              <Input id="create-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-hubspot-id">HubSpot contact ID</Label>
            <Input
              id="create-hubspot-id"
              value={hubspotContactId}
              onChange={(e) => setHubspotContactId(e.target.value)}
              placeholder="Optional existing contact ID"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-credits-usd">Credits (USD)</Label>
            {invite && inviteMethod === "SIGNUP_INVITE" ? (
              <p className="text-muted-foreground text-xs">
                Credits cannot be pre-set for signup invite users. Grant credits manually after the user accepts their
                invitation.
              </p>
            ) : (
              <Input
                id="create-credits-usd"
                type="number"
                min={0}
                step={1}
                value={creditsUsd}
                onChange={(e) => setCreditsUsd(e.target.value)}
                placeholder="Optional — whole number only"
              />
            )}
          </div>

          {/* Org combobox */}
          <div className="grid gap-2">
            <Label htmlFor="create-org-input">Org</Label>
            <div ref={orgRef} className="relative">
              {orgId && !orgOpen ? (
                <div className="border-input flex h-9 w-full items-center gap-2 rounded-md border bg-transparent px-3 text-sm shadow-xs">
                  <span className="flex-1 truncate">{selectedOrgName}</span>
                  <button
                    type="button"
                    onClick={handleOrgClear}
                    className="text-muted-foreground hover:text-foreground shrink-0 text-xs"
                    aria-label="Clear org selection"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <Input
                  id="create-org-input"
                  autoComplete="off"
                  placeholder={orgOpen ? "Search by name, email, or UUID…" : "New org (default)"}
                  value={orgQuery}
                  onChange={(e) => {
                    setOrgQuery(e.target.value);
                    setOrgOpen(true);
                  }}
                  onFocus={() => setOrgOpen(true)}
                />
              )}

              {orgOpen && (
                <div className="bg-popover absolute top-full left-0 right-0 z-50 mt-1 overflow-hidden rounded-md border shadow-md">
                  <Command shouldFilter={false}>
                    <CommandList>
                      <CommandGroup>
                        <CommandItem
                          value="__new__"
                          onSelect={() => handleOrgSelect("")}
                          className={cn(!orgId && "bg-accent")}
                        >
                          <span className="font-medium">New org</span>
                          <span className="text-muted-foreground ml-2 text-xs">auto-provisioned (default)</span>
                        </CommandItem>
                      </CommandGroup>

                      {filteredOrgs.length > 0 && (
                        <CommandGroup heading="Existing orgs">
                          {filteredOrgs.map(([id, { name, emails }]) => (
                            <CommandItem
                              key={id}
                              value={id}
                              onSelect={() => handleOrgSelect(id)}
                              className={cn(orgId === id && "bg-accent")}
                            >
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <span className="font-medium">{name}</span>
                                <span className="text-muted-foreground truncate font-mono text-xs">{id}</span>
                                {emails.length > 0 && (
                                  <span className="text-muted-foreground truncate text-xs">
                                    {emails.slice(0, 3).join(", ")}
                                    {emails.length > 3 && ` +${emails.length - 3} more`}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}

                      {filteredOrgs.length === 0 && orgQuery.trim() && (
                        <CommandEmpty>No orgs match "{orgQuery}"</CommandEmpty>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="create-invite" checked={invite} onCheckedChange={(v) => setInvite(v === true)} />
            <Label htmlFor="create-invite">Send invite</Label>
          </div>

          {invite && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="invite-method">Invite method</Label>
                <select
                  id="invite-method"
                  className={nativeSelectClass}
                  value={inviteMethod}
                  onChange={(e) => {
                    const method = e.target.value as "SIGNUP_INVITE" | "PASSWORD_RESET";
                    setInviteMethod(method);
                    if (method === "SIGNUP_INVITE") setCreditsUsd("");
                  }}
                >
                  <option value="SIGNUP_INVITE">WorkOS signup link (recommended)</option>
                  <option value="PASSWORD_RESET">Password setup email</option>
                </select>
                <p className="text-muted-foreground text-xs">
                  {inviteMethod === "PASSWORD_RESET"
                    ? "Sends a password creation link. Expires in 15 minutes."
                    : "Sends a signup link via WorkOS. The user can sign in with Google, GitHub, or create a password. Expires in 7 days."}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="invited-by">Invited by</Label>
                <div className="flex gap-2">
                  <Input
                    id="invited-by"
                    type="email"
                    value={invitedBy}
                    onChange={(e) => {
                      setInvitedBy(e.target.value);
                      setInvitedByBlurred(false);
                    }}
                    onBlur={() => setInvitedByBlurred(true)}
                    placeholder={ADMIN_DEFAULT_INVITER}
                    className={cn("flex-1", inviterNotFound && "border-yellow-500")}
                  />
                  {me?.email && me.email !== invitedBy && (
                    <button
                      type="button"
                      title={`Switch to ${me.email}`}
                      onClick={() => setInvitedBy(me.email)}
                      className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded-md border px-2 text-xs transition-colors"
                    >
                      <IconUserShare className="size-3.5" />
                      Use mine
                    </button>
                  )}
                  {invitedBy !== ADMIN_DEFAULT_INVITER && (
                    <button
                      type="button"
                      title={`Reset to ${ADMIN_DEFAULT_INVITER}`}
                      onClick={() => setInvitedBy(ADMIN_DEFAULT_INVITER)}
                      className="text-muted-foreground hover:text-foreground rounded-md border px-2 text-xs transition-colors"
                    >
                      Reset
                    </button>
                  )}
                </div>
                {inviterNotFound ? (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">
                    This email is not recognised as a user in the system. Invitations may fail if the WorkOS account is
                    missing or the ID is stale.
                  </p>
                ) : (
                  <p className="text-muted-foreground text-xs">
                    The WorkOS user whose name appears on the invitation email.
                  </p>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending || emailAlreadyExists}>
              <IconUserPlus className="mr-1 size-4" />
              {invite ? "Invite user" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
